import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { RegistrationEditModal, RegistrationRow } from './RegistrationEditModal';
import { GuestAccountsPanel } from './GuestAccountsPanel';
import { MatchSuggestionsPanel } from './MatchSuggestionsPanel';
import { UserAccountsPanel } from './UserAccountsPanel';
import { RegistrationIntegrityAuditModal } from './RegistrationIntegrityAuditModal';
import { PRIMARY, TEXT_MUTED, BORDER_LIGHT } from './table-management/designTokens';

const LEGACY_BALL_EVENT_ID = 'e1000000-0000-0000-0000-000000000003';

type RegistrationSubView = 'directory' | 'guest-accounts' | 'linking' | 'user-accounts';

const REG_SUB_VIEWS: { key: RegistrationSubView; label: string; tooltip: string }[] = [
  { key: 'directory', label: 'Directory', tooltip: 'Registrations table, search, Register User, edit modal' },
  { key: 'guest-accounts', label: 'Guest Accounts', tooltip: 'Provisioning panel' },
  { key: 'linking', label: 'Linking', tooltip: 'Match Suggestions panel (auto-linking registrations ↔ guest records)' },
  { key: 'user-accounts', label: 'User Accounts', tooltip: 'User Accounts panel (password resets, synthetic account management)' },
];

interface MealOptionItem {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

interface UserItem {
  id: string;
  fullName: string;
  email: string;
}

interface TicketTierItem {
  id: string;
  name: string;
  price: number | string;
  description: string;
  active: boolean;
}

// Local editable state per row
interface RowEditState {
  tierId: string;
  mealOptionId: string;
  tableNumber: string;
  seatNumber: string;
}

const styles: Record<string, React.CSSProperties> = {
  container: { marginTop: '0.5rem' },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  filterRow: {
    display: 'flex',
    gap: '0.4rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  filterPill: {
    padding: '0.35rem 0.75rem',
    border: '1px solid #d0d0d0',
    borderRadius: '16px',
    fontSize: '0.78rem',
    fontWeight: 500,
    cursor: 'pointer',
    backgroundColor: '#fff',
    color: '#555',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'all 0.15s',
  },
  filterPillActive: {
    padding: '0.35rem 0.75rem',
    border: '1px solid #1a6b3c',
    borderRadius: '16px',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    backgroundColor: '#e8f5e9',
    color: '#1a6b3c',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'all 0.15s',
  },
  filterCount: {
    fontSize: '0.7rem',
    backgroundColor: 'rgba(0,0,0,0.08)',
    borderRadius: '8px',
    padding: '1px 5px',
    minWidth: '18px',
    textAlign: 'center',
  },
  searchInput: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
    width: '100%',
    maxWidth: '400px',
  },
  registerBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#0f2d1f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.9rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.75rem 0.5rem',
    borderBottom: '2px solid #e0e0e0',
    color: '#0f2d1f',
    fontWeight: 600,
    fontSize: '0.85rem',
  },
  td: {
    padding: '0.6rem 0.5rem',
    borderBottom: '1px solid #f0f0f0',
    verticalAlign: 'middle' as const,
  },
  select: {
    padding: '0.35rem 0.5rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.85rem',
    minWidth: '130px',
  },
  numberInput: {
    padding: '0.35rem 0.5rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.85rem',
    width: '60px',
  },
  saveBtn: {
    padding: '0.3rem 0.6rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  saveBtnDisabled: {
    padding: '0.3rem 0.6rem',
    backgroundColor: '#ccc',
    color: '#666',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  savedIndicator: {
    fontSize: '0.75rem',
    color: '#2e7d32',
    fontWeight: 500,
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  inlineError: {
    color: '#d32f2f',
    fontSize: '0.75rem',
    marginTop: '0.2rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#666',
    fontSize: '1rem',
  },
  dirtyBorder: {
    border: '1px solid #c9a84c',
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed' as const,
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  modalTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    marginBottom: '1rem',
    color: '#0f2d1f',
  },
  modalInput: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
    width: '100%',
    marginBottom: '0.75rem',
  },
  userList: {
    maxHeight: '300px',
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    marginBottom: '1rem',
  },
  userItem: {
    padding: '0.6rem 0.75rem',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  userItemName: { fontWeight: 500, fontSize: '0.9rem' },
  userItemEmail: { color: '#666', fontSize: '0.8rem' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' },
  cancelBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  successMsg: {
    color: '#2e7d32',
    backgroundColor: '#e8f5e9',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '0.75rem',
    fontSize: '0.85rem',
  },
  subNav: {
    display: 'flex',
    gap: '0',
    marginBottom: '1rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  subTab: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: TEXT_MUTED,
    borderBottom: '2px solid transparent',
    marginBottom: '-1px',
    transition: 'color 0.2s, border-color 0.2s',
  },
  subTabActive: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: PRIMARY,
    borderBottom: `2px solid ${PRIMARY}`,
    marginBottom: '-1px',
    transition: 'color 0.2s, border-color 0.2s',
  },
};

export function RegistrationsTab() {
  const { user } = useAuth();
  const canEdit = user?.role === 'Organizer' || user?.role === 'Admin';

  const [registrations, setRegistrations] = useState<RegistrationRow[]>([]);
  const [mealOptions, setMealOptions] = useState<MealOptionItem[]>([]);
  const [ticketTiers, setTicketTiers] = useState<TicketTierItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // Edit modal state
  const [selectedRegistration, setSelectedRegistration] = useState<RegistrationRow | null>(null);

  // Per-row edit state
  const [rowEdits, setRowEdits] = useState<Record<string, RowEditState>>({});
  // Per-row error/success messages
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [rowSaving, setRowSaving] = useState<Record<string, boolean>>({});
  const [rowSaved, setRowSaved] = useState<Record<string, boolean>>({});

  // Register User modal state
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [users, setUsers] = useState<UserItem[]>([]);

  // Audit modal state
  const [showAuditModal, setShowAuditModal] = useState(false);

  // Filter state
  const [activeFilter, setActiveFilter] = useState<'all' | 'no-meal' | 'patron' | 'general' | 'no-seat'>('all');
  const [userSearch, setUserSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [suggestionsRefresh, setSuggestionsRefresh] = useState(0);
  const [activeView, setActiveView] = useState<RegistrationSubView>('directory');

  // Initialize row edit state from registration data
  function buildRowEdit(reg: RegistrationRow): RowEditState {
    // Find the tier ID that matches the ticket's tierName
    const matchingTier = ticketTiers.find((t) => t.name === reg.ticket?.tierName);
    return {
      tierId: matchingTier?.id ?? '',
      mealOptionId: reg.mealSelection?.mealOptionId ?? '',
      tableNumber: reg.seatAssignment?.tableNumber?.toString() ?? '',
      seatNumber: reg.seatAssignment?.seatNumber?.toString() ?? '',
    };
  }

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        const [regs, meals, tiers] = await Promise.all([
          apiClient<RegistrationRow[]>('/legacy-ball/registrations'),
          apiClient<MealOptionItem[]>('/legacy-ball/meal-options'),
          apiClient<TicketTierItem[]>('/legacy-ball/tiers'),
        ]);
        if (!cancelled) {
          setRegistrations(regs);
          setMealOptions(meals.filter((m) => m.active));
          setTicketTiers(tiers);
          // Initialize edit state for each row
          const edits: Record<string, RowEditState> = {};
          for (const reg of regs) {
            const matchingTier = tiers.find((t) => t.name === reg.ticket?.tierName);
            edits[reg.id] = {
              tierId: matchingTier?.id ?? '',
              mealOptionId: reg.mealSelection?.mealOptionId ?? '',
              tableNumber: reg.seatAssignment?.tableNumber?.toString() ?? '',
              seatNumber: reg.seatAssignment?.seatNumber?.toString() ?? '',
            };
          }
          setRowEdits(edits);
        }
      } catch {
        if (!cancelled) setFetchError('Could not load registrations.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, []);

  // Check if a row has unsaved changes
  function isDirty(reg: RegistrationRow): boolean {
    const edit = rowEdits[reg.id];
    if (!edit) return false;

    const savedTierId = ticketTiers.find((t) => t.name === reg.ticket?.tierName)?.id ?? '';
    const savedMealId = reg.mealSelection?.mealOptionId ?? '';
    const savedTable = reg.seatAssignment?.tableNumber?.toString() ?? '';
    const savedSeat = reg.seatAssignment?.seatNumber?.toString() ?? '';

    return (
      edit.tierId !== savedTierId ||
      edit.mealOptionId !== savedMealId ||
      edit.tableNumber !== savedTable ||
      edit.seatNumber !== savedSeat
    );
  }

  function updateRowEdit(regId: string, field: keyof RowEditState, value: string) {
    setRowEdits((prev) => ({
      ...prev,
      [regId]: { ...prev[regId], [field]: value },
    }));
    // Clear saved indicator when editing
    setRowSaved((prev) => ({ ...prev, [regId]: false }));
    setRowErrors((prev) => ({ ...prev, [regId]: '' }));
  }

  // --- Save Row ---
  async function handleSaveRow(reg: RegistrationRow) {
    const edit = rowEdits[reg.id];
    if (!edit) return;

    setRowSaving((prev) => ({ ...prev, [reg.id]: true }));
    setRowErrors((prev) => ({ ...prev, [reg.id]: '' }));
    setRowSaved((prev) => ({ ...prev, [reg.id]: false }));

    try {
      // 1. Ticket assignment (if tier changed)
      const savedTierId = ticketTiers.find((t) => t.name === reg.ticket?.tierName)?.id ?? '';
      if (edit.tierId && edit.tierId !== savedTierId) {
        const ticketResult = await apiClient<{ tierName: string; priceAtPurchase: number | string }>('/legacy-ball/tickets', {
          method: 'POST',
          body: { registrationId: reg.id, tierId: edit.tierId },
        });
        reg = { ...reg, ticket: { tierName: ticketResult.tierName, priceAtPurchase: Number(ticketResult.priceAtPurchase) } };
      }

      // 2. Meal selection (if changed)
      const savedMealId = reg.mealSelection?.mealOptionId ?? '';
      if (edit.mealOptionId !== savedMealId) {
        if (edit.mealOptionId) {
          await apiClient('/legacy-ball/meal-selections', {
            method: 'POST',
            body: { registrationId: reg.id, mealOptionId: edit.mealOptionId },
          });
          const mealName = mealOptions.find((m) => m.id === edit.mealOptionId)?.name ?? '';
          reg = { ...reg, mealSelection: { mealOptionId: edit.mealOptionId, mealOptionName: mealName } };
        } else {
          reg = { ...reg, mealSelection: null };
        }
      }

      // 3. Seat assignment (if both table and seat are provided and changed)
      const tableNum = edit.tableNumber.trim();
      const seatNum = edit.seatNumber.trim();
      const savedTable = reg.seatAssignment?.tableNumber?.toString() ?? '';
      const savedSeat = reg.seatAssignment?.seatNumber?.toString() ?? '';

      if (tableNum && seatNum && (tableNum !== savedTable || seatNum !== savedSeat)) {
        const tableNumber = parseInt(tableNum, 10);
        const seatNumber = parseInt(seatNum, 10);
        if (!isNaN(tableNumber) && !isNaN(seatNumber)) {
          if (seatNumber < 1 || seatNumber > 10) {
            throw { message: 'Seat number must be between 1 and 10 (max 10 seats per table).' };
          }
          await apiClient('/legacy-ball/seats', {
            method: 'POST',
            body: { registrationId: reg.id, tableNumber, seatNumber },
          });
          reg = { ...reg, seatAssignment: { tableNumber, seatNumber } };
        }
      }

      // Update registrations state with saved values
      setRegistrations((prev) => prev.map((r) => (r.id === reg.id ? reg : r)));
      // Update edit state to match saved values
      setRowEdits((prev) => ({ ...prev, [reg.id]: buildRowEdit(reg) }));
      setRowSaved((prev) => ({ ...prev, [reg.id]: true }));

      // Clear saved indicator after 3 seconds
      setTimeout(() => {
        setRowSaved((prev) => ({ ...prev, [reg.id]: false }));
      }, 3000);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setRowErrors((prev) => ({ ...prev, [reg.id]: apiErr.message || 'Failed to save.' }));
    } finally {
      setRowSaving((prev) => ({ ...prev, [reg.id]: false }));
    }
  }

  // --- Register User Modal ---
  async function openRegisterModal() {
    setShowRegisterModal(true);
    setUserSearch('');
    setRegisterError('');
    setRegisterSuccess('');
    setLoadingUsers(true);

    try {
      const allUsers = await apiClient<UserItem[]>('/users');
      const registeredEmails = new Set(registrations.filter((r) => r.email).map((r) => r.email));
      setUsers(allUsers.filter((u) => !registeredEmails.has(u.email)));
    } catch {
      setRegisterError('Could not load users.');
    } finally {
      setLoadingUsers(false);
    }
  }

  async function handleRegisterUser(userId: string) {
    setRegisterError('');
    setRegisterSuccess('');

    try {
      const newReg = await apiClient<Partial<RegistrationRow>>('/legacy-ball/register-user', {
        method: 'POST',
        body: { userId },
      });

      const fullReg: RegistrationRow = {
        id: newReg.id!,
        fullName: newReg.fullName!,
        email: newReg.email!,
        status: 'Confirmed',
        dietaryPrefs: null,
        chapterAffiliation: null,
        mealSelection: newReg.mealSelection ?? null,
        seatAssignment: newReg.seatAssignment ?? null,
        ticket: newReg.ticket ?? null,
      };

      setRegistrations((prev) => [...prev, fullReg]);
      setRowEdits((prev) => ({
        ...prev,
        [fullReg.id]: { tierId: '', mealOptionId: '', tableNumber: '', seatNumber: '' },
      }));
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      setRegisterSuccess(`${fullReg.fullName} has been registered for the Legacy Ball.`);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setRegisterError(apiErr.message || 'Failed to register user.');
    }
  }

  const filteredUsers = users.filter((u) => {
    if (!userSearch.trim()) return true;
    const q = userSearch.toLowerCase();
    return u.fullName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  // --- Filtering ---
  const filteredRegistrations = registrations.filter((reg) => {
    // Text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = reg.fullName.toLowerCase().includes(query) || (reg.email || '').toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }
    // Category filter
    switch (activeFilter) {
      case 'no-meal':
        return !reg.mealSelection;
      case 'patron':
        return reg.ticket?.tierName?.toLowerCase().includes('patron');
      case 'general':
        return reg.ticket?.tierName?.toLowerCase().includes('general') || !reg.ticket?.tierName;
      case 'no-seat':
        return !reg.seatAssignment;
      default:
        return true;
    }
  });

  if (loading) return <div style={styles.loading}>Loading registrations...</div>;
  if (fetchError) return <div style={styles.error}>{fetchError}</div>;

  return (
    <div style={styles.container}>
      {canEdit && (
        <nav style={styles.subNav} aria-label="Registration views">
          {REG_SUB_VIEWS.map((view) => (
            <button
              key={view.key}
              style={activeView === view.key ? styles.subTabActive : styles.subTab}
              onClick={() => setActiveView(view.key)}
              aria-current={activeView === view.key ? 'page' : undefined}
              title={view.tooltip}
            >
              {view.label}
            </button>
          ))}
        </nav>
      )}

      {/* Guest Accounts view */}
      {canEdit && activeView === 'guest-accounts' && (
        <GuestAccountsPanel eventId={LEGACY_BALL_EVENT_ID} onProvisioned={() => setSuggestionsRefresh((k) => k + 1)} />
      )}

      {/* Linking view */}
      {canEdit && activeView === 'linking' && (
        <MatchSuggestionsPanel eventId={LEGACY_BALL_EVENT_ID} refreshTrigger={suggestionsRefresh} />
      )}

      {/* User Accounts view */}
      {canEdit && activeView === 'user-accounts' && (
        <UserAccountsPanel />
      )}

      {/* Directory view (default, also shown for non-editors) */}
      {(activeView === 'directory' || !canEdit) && (
        <>
          <div style={styles.headerRow}>
            <input
              type="text"
              placeholder="Search by name or email..."
              style={styles.searchInput}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {canEdit && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  style={{ ...styles.registerBtn, backgroundColor: '#455a64' }}
                  onClick={() => setShowAuditModal(true)}
                >
                  🔍 Integrity Audit
                </button>
                <button style={styles.registerBtn} onClick={openRegisterModal}>
                  + Register User
                </button>
              </div>
            )}
          </div>

          {/* Filter pills */}
          <div style={styles.filterRow}>
            {([
              { key: 'all', label: 'All' },
              { key: 'no-meal', label: 'No Meal' },
              { key: 'patron', label: 'Patron' },
              { key: 'general', label: 'General' },
              { key: 'no-seat', label: 'No Seat' },
            ] as const).map((f) => (
              <button
                key={f.key}
                style={activeFilter === f.key ? styles.filterPillActive : styles.filterPill}
                onClick={() => setActiveFilter(f.key)}
              >
                {f.label}
                {f.key !== 'all' && (
                  <span style={styles.filterCount}>
                    {f.key === 'no-meal' ? registrations.filter(r => !r.mealSelection).length
                      : f.key === 'patron' ? registrations.filter(r => r.ticket?.tierName?.toLowerCase().includes('patron')).length
                      : f.key === 'general' ? registrations.filter(r => r.ticket?.tierName?.toLowerCase().includes('general') || !r.ticket?.tierName).length
                      : registrations.filter(r => !r.seatAssignment).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Chapter</th>
                <th style={styles.th}>Ticket #</th>
                <th style={styles.th}>Ticket Tier</th>
                <th style={styles.th}>Meal</th>
                <th style={styles.th}>Table #</th>
                <th style={styles.th}>Seat #</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {filteredRegistrations.map((reg, idx) => {
                const edit = rowEdits[reg.id] || { tierId: '', mealOptionId: '', tableNumber: '', seatNumber: '' };
                const dirty = isDirty(reg);
                const saving = rowSaving[reg.id];
                const saved = rowSaved[reg.id];
                const error = rowErrors[reg.id];

                return (
                  <React.Fragment key={reg.id}>
                    <tr
                      style={{
                        backgroundColor: dirty ? '#fffde7' : !reg.mealSelection ? '#fff3e0' : idx % 2 === 0 ? '#ffffff' : '#f7faf8',
                        ...(canEdit ? { cursor: 'pointer' } : {}),
                        transition: 'background-color 0.15s',
                      }}
                      onClick={() => canEdit && setSelectedRegistration(reg)}
                    >
                      <td style={styles.td}>{reg.fullName}</td>
                      <td style={styles.td}>{reg.email || '—'}</td>
                      <td style={{ ...styles.td, color: reg.chapterAffiliation ? '#333' : '#999', fontStyle: reg.chapterAffiliation ? 'normal' : 'italic' }}>
                        {reg.chapterAffiliation || 'Unaffiliated'}
                      </td>
                      <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {reg.ticket?.ticketNumber ? `TKT-${String(reg.ticket.ticketNumber).padStart(3, '0')}` : '—'}
                      </td>
                      <td style={styles.td}>
                        <select
                          style={{ ...styles.select, ...(dirty && edit.tierId !== (ticketTiers.find((t) => t.name === reg.ticket?.tierName)?.id ?? '') ? styles.dirtyBorder : {}) }}
                          value={edit.tierId}
                          onChange={(e) => updateRowEdit(reg.id, 'tierId', e.target.value)}
                        >
                          <option value="">Select tier...</option>
                          {ticketTiers.map((tier) => (
                            <option key={tier.id} value={tier.id}>
                              {tier.name} — ${Number(tier.price).toFixed(2)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={styles.td}>
                        <select
                          style={{ ...styles.select, ...(dirty && edit.mealOptionId !== (reg.mealSelection?.mealOptionId ?? '') ? styles.dirtyBorder : {}) }}
                          value={edit.mealOptionId}
                          onChange={(e) => updateRowEdit(reg.id, 'mealOptionId', e.target.value)}
                        >
                          <option value="">Select meal...</option>
                          {mealOptions.map((meal) => (
                            <option key={meal.id} value={meal.id}>
                              {meal.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          style={{ ...styles.numberInput, ...(dirty && edit.tableNumber !== (reg.seatAssignment?.tableNumber?.toString() ?? '') ? styles.dirtyBorder : {}) }}
                          value={edit.tableNumber}
                          onChange={(e) => updateRowEdit(reg.id, 'tableNumber', e.target.value)}
                          min={1}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          style={{ ...styles.numberInput, ...(dirty && edit.seatNumber !== (reg.seatAssignment?.seatNumber?.toString() ?? '') ? styles.dirtyBorder : {}) }}
                          value={edit.seatNumber}
                          onChange={(e) => updateRowEdit(reg.id, 'seatNumber', e.target.value)}
                          min={1}
                          max={10}
                          placeholder="1-10"
                        />
                      </td>
                      <td style={styles.td}>
                        {saved ? (
                          <span style={styles.savedIndicator}>✓ Saved</span>
                        ) : (
                          <button
                            style={dirty && !saving ? styles.saveBtn : styles.saveBtnDisabled}
                            onClick={() => handleSaveRow(reg)}
                            disabled={!dirty || saving}
                          >
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {error && (
                      <tr>
                        <td colSpan={8} style={{ padding: '0.25rem 0.5rem', border: 'none' }}>
                          <div style={styles.inlineError}>{error}</div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {filteredRegistrations.length === 0 && registrations.length > 0 && (
            <p style={{ color: '#666', textAlign: 'center', padding: '1rem' }}>
              No registrations match your search.
            </p>
          )}

          {registrations.length === 0 && (
            <p style={{ color: '#666', textAlign: 'center', padding: '1rem' }}>
              No registrations found. Use the "Register User" button to add attendees.
            </p>
          )}

          {/* Register User Modal */}
          {showRegisterModal && (
            <div style={styles.modalOverlay} onClick={() => setShowRegisterModal(false)}>
              <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div style={styles.modalTitle}>Register User for Legacy Ball</div>

                {registerSuccess && <div style={styles.successMsg}>{registerSuccess}</div>}
                {registerError && <div style={styles.error}>{registerError}</div>}

                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  style={styles.modalInput}
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  autoFocus
                />

                {loadingUsers ? (
                  <p style={{ color: '#666', textAlign: 'center', padding: '1rem' }}>Loading users...</p>
                ) : (
                  <div style={styles.userList}>
                    {filteredUsers.length === 0 ? (
                      <p style={{ color: '#666', textAlign: 'center', padding: '1rem' }}>
                        {users.length === 0 ? 'All users are already registered.' : 'No users match your search.'}
                      </p>
                    ) : (
                      filteredUsers.map((user) => (
                        <div
                          key={user.id}
                          style={styles.userItem}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                        >
                          <div>
                            <div style={styles.userItemName}>{user.fullName}</div>
                            <div style={styles.userItemEmail}>{user.email}</div>
                          </div>
                          <button
                            style={{ ...styles.registerBtn, padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}
                            onClick={() => handleRegisterUser(user.id)}
                          >
                            Register
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}

                <div style={styles.modalActions}>
                  <button style={styles.cancelBtn} onClick={() => setShowRegisterModal(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Registration Edit Modal */}
          {selectedRegistration && (
            <RegistrationEditModal
              registration={selectedRegistration}
              mealOptions={mealOptions}
              ticketTiers={ticketTiers}
              onClose={() => setSelectedRegistration(null)}
              onSaved={(updated) => {
                setRegistrations((prev) =>
                  prev.map((r) => (r.id === updated.id ? updated : r)),
                );
                setRowEdits((prev) => ({
                  ...prev,
                  [updated.id]: buildRowEdit(updated),
                }));
                setSelectedRegistration(null);
              }}
            />
          )}
        </>
      )}

      {/* Registration Integrity Audit Modal */}
      <RegistrationIntegrityAuditModal
        open={showAuditModal}
        onClose={() => setShowAuditModal(false)}
      />
    </div>
  );
}
